// frontend/src/pages/EmployersPage.jsx
// Updated with contact_status tracking for Placement Management
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import api from '../../utils/api';
import EmployerForm from "./EmployerForm";
import EmployerList from "./EmployerList";

// Contact status options
const CONTACT_STATUSES = [
  {
    value: "new",
    label: "New",
    color: "secondary",
    description: "Just added, no contact yet",
  },
  {
    value: "contacted",
    label: "Contacted",
    color: "info",
    description: "Initial outreach made",
  },
  {
    value: "no_answer",
    label: "No Answer",
    color: "warning",
    description: "Attempted but no response",
  },
  {
    value: "not_interested",
    label: "Not Interested",
    color: "danger",
    description: "Employer declined",
  },
  {
    value: "do_not_contact",
    label: "Do Not Contact",
    color: "dark",
    description: "Requested no further contact",
  },
  {
    value: "interested",
    label: "Interested",
    color: "primary",
    description: "Employer expressed interest",
  },
  {
    value: "pending_opening",
    label: "Pending Opening",
    color: "info",
    description: "Interested but no current openings",
  },
  {
    value: "has_opening",
    label: "Has Opening",
    color: "success",
    description: "Has a position available",
  },
  {
    value: "placement_made",
    label: "Placement Made",
    color: "success",
    description: "Successfully placed a consumer",
  },
];

const EmployersPage = () => {
  const { user } = useContext(AuthContext);
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployer, setEditingEmployer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const tenant_id = user?.tenant_id;

  useEffect(() => {
    if (tenant_id) {
      loadEmployers();
    }
  }, [tenant_id, statusFilter, showActiveOnly]);

  const loadEmployers = async () => {
    setLoading(true);
    try {
      const params = { tenant_id };
      if (showActiveOnly) params.is_active = "true";
      if (statusFilter) params.contact_status = statusFilter;

      const response = await api.get("/employers", { params });
      setEmployers(response.data);
      setError(null);
    } catch (err) {
      console.error("Error loading employers:", err);
      setError("Failed to load employers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingEmployer(null);
    setShowForm(true);
  };

  const handleEdit = (employer) => {
    setEditingEmployer(employer);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEmployer(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingEmployer(null);
    loadEmployers();
  };

  const handleDelete = async (employer) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${employer.company_name}?`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/employers/${employer.employer_id}`, {
        params: { tenant_id },
      });
      loadEmployers();
    } catch (err) {
      console.error("Error deleting employer:", err);
      alert("Failed to delete employer");
    }
  };

  const handleStatusChange = async (employer, newStatus) => {
    try {
      await api.put(`/employers/${employer.employer_id}`, {
        ...employer,
        contact_status: newStatus,
        tenant_id,
        updated_by: user?.user_id,
      });
      loadEmployers();
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status");
    }
  };

  // Filter employers by search term
  const filteredEmployers = employers.filter((emp) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      emp.company_name?.toLowerCase().includes(term) ||
      emp.contact_name?.toLowerCase().includes(term) ||
      emp.industry?.toLowerCase().includes(term) ||
      emp.city?.toLowerCase().includes(term)
    );
  });

  // Calculate status counts
  const statusCounts = employers.reduce((acc, emp) => {
    const status = emp.contact_status || "new";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  if (showForm) {
    return (
      <EmployerForm
        employer={editingEmployer}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        contactStatuses={CONTACT_STATUSES}
      />
    );
  }

  return (
    <div className="container-fluid px-2 px-md-4 py-3 py-md-4">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 mb-md-4 gap-2">
        <div>
          <h2 className="mb-0 h4 h-md-2">
            <i className="bi bi-building me-2"></i>
            <span className="d-none d-sm-inline">Contacts (</span>Employers<span className="d-none d-sm-inline">)</span>
          </h2>
          <p className="text-muted mb-0 small d-none d-md-block">
            Manage employer contacts and track outreach status
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={handleCreate}>
          <i className="bi bi-plus-circle me-1 me-sm-2"></i>
          <span className="d-none d-sm-inline">Add </span>Employer
        </button>
      </div>

      {/* Status Summary Cards - Horizontal scroll on mobile */}
      <div className="mb-3 mb-md-4" style={{ overflowX: 'auto' }}>
        <div className="d-flex gap-2" style={{ minWidth: 'max-content' }}>
          {CONTACT_STATUSES.slice(0, 5).map((status) => (
            <div
              key={status.value}
              className={`card border-${status.color} ${
                statusFilter === status.value
                  ? "bg-" + status.color + " text-white"
                  : ""
              }`}
              style={{ cursor: "pointer", minWidth: '80px', flex: '1 0 auto' }}
              onClick={() =>
                setStatusFilter(
                  statusFilter === status.value ? "" : status.value
                )
              }
            >
              <div className="card-body py-2 px-2 text-center">
                <h5 className="mb-0 h6">{statusCounts[status.value] || 0}</h5>
                <small style={{ fontSize: '0.7rem' }}>{status.label}</small>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3 mb-md-4">
        <div className="card-body p-2 p-md-3">
          <div className="row g-2 align-items-center">
            <div className="col-8 col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchTerm("")}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-4 col-md-3">
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                {CONTACT_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label} ({statusCounts[status.value] || 0})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <div className="form-check form-check-inline">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="showActiveOnly"
                  checked={showActiveOnly}
                  onChange={(e) => setShowActiveOnly(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="showActiveOnly">
                  <span className="d-none d-sm-inline">Active </span>Only
                </label>
              </div>
            </div>
            <div className="col-6 col-md-2 text-end">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("");
                  setShowActiveOnly(true);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert-danger py-2 small" role="alert">
          {error}
        </div>
      )}

      {/* Employer List */}
      <EmployerList
        employers={filteredEmployers}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        contactStatuses={CONTACT_STATUSES}
        searchTerm={searchTerm}
        onCreate={handleCreate}
      />
    </div>
  );
};

export default EmployersPage;